import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Collection } from "../../../../entity/Collection";
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  VStack,
  HStack,
  Button,
  Select,
  IconButton,
  Grid,
  Box,
  Text,
  Badge,
  Checkbox,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { DeleteIcon, AddIcon } from "@chakra-ui/icons";
import { Method } from "../../../../services/configuration/Configuration";
import { DeliveryFragment } from "../../../../entity/DeliveryFragment";
import { useSelector } from "react-redux";
import { selectDeliveryCompanies } from "../../../../store/selectors/DeliverySelector";

interface CreateUpdateCollectionProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (collection: Collection) => void;
  collection: Collection | null;
}

export const CreateUpdateCollection: React.FC<CreateUpdateCollectionProps> = ({
  isOpen,
  onClose,
  onSave,
  collection,
}) => {
  const { t } = useTranslation();
  const [newCollection, setNewCollection] = useState<Collection>({
    name: "",
    description: "",
    type: Method.POST,
    isActive: true,
    fields: [],
    companyId: "",
    path: "",
  });
  const [activeTab, setActiveTab] = useState(0); // 0 for header, 1 for body

  const fields = useSelector(selectDeliveryCompanies);

  useEffect(() => {
    if (collection) {
      setNewCollection(collection);
    } else {
      setNewCollection({
        name: "",
        description: "",
        type: Method.POST,
        isActive: true,
        fields: [],
        companyId: fields?.id || "",
        path: "",
      });
    }
  }, [collection, isOpen]);

  // Filter fields by section
  const headerFields = newCollection.fields.filter((field) => !field.inBody);
  const bodyFields = newCollection.fields.filter((field) => field.inBody);

  const addField = (field: DeliveryFragment, inBody: boolean) => {
    const newField: DeliveryFragment = {
      label: field.label,
      type: field.type,
      isRequired: field.isRequired,
      key: field.key,
      inBody: inBody,
    };
    console.log(newField);
    setNewCollection((prev) => ({
      ...prev,
      fields: [...prev.fields, newField],
    }));
  };

  const updateField = (index: number, field: DeliveryFragment) => {
    setNewCollection((prev) => ({
      ...prev,
      fields: prev.fields.map((f, i) => (i === index ? field : f)),
    }));
  };

  const removeField = (index: number) => {
    setNewCollection((prev) => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index),
    }));
  };

  const handleSave = () => {
    if (newCollection.name.trim()) {
      onSave(newCollection);
    }
  };

  const addFieldToCollection = (fieldId: string, inBody: boolean) => {
    addField(
      fields?.fragments?.filter((field) => field.id === fieldId)[0]!,
      inBody,
    );
  };

  const renderFieldSection = (inBody: boolean) => {
    const sectionFields = inBody ? bodyFields : headerFields;
    const sectionTitle = inBody
      ? t("collection.body") || "Body"
      : t("collection.header") || "Header";
    const availableFields =
      fields?.fragments.filter(
        (field) => !newCollection.fields.some((f) => f.id === field.id),
      ) || [];

    return (
      <VStack spacing={4} align="stretch">
        <HStack justify="space-between" align="center">
          <Text fontWeight="semibold">
            {sectionTitle} ({sectionFields.length})
          </Text>
          <Button
            leftIcon={<AddIcon />}
            size="sm"
            onClick={() => {}}
            colorScheme="blue"
          >
            {t("collection.addField") || "Add Field"}
          </Button>
        </HStack>

        {/* Available fields to add */}
        {availableFields.length > 0 && (
          <Box>
            <Text fontSize="sm" color="gray.600" mb={2}>
              {t("collection.availableFields") || "Available Fields"}
            </Text>
            <Grid
              templateColumns="repeat(3, 1fr)"
              border="1px solid"
              borderColor="gray.200"
              gap={2}
              p={3}
              borderRadius="md"
            >
              {availableFields.map((field) => (
                <Checkbox
                  key={field.id}
                  size="sm"
                  onChange={() => addFieldToCollection(field.id!!, inBody)}
                >
                  {field.label}
                </Checkbox>
              ))}
            </Grid>
          </Box>
        )}

        {/* Current fields in this section */}
        {sectionFields.length > 0 && (
          <Box>
            <Text fontSize="sm" color="gray.600" mb={2}>
              {t("collection.currentFields") || "Current Fields"}
            </Text>
            <VStack spacing={2} align="stretch">
              {sectionFields.map((field, index) => {
                const globalIndex = newCollection.fields.findIndex(
                  (f) => f === field,
                );
                return (
                  <Box
                    key={globalIndex}
                    border="1px solid"
                    borderColor="gray.200"
                    p={3}
                    borderRadius="md"
                  >
                    <HStack justify="space-between" align="center">
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">{field.label}</Text>
                        <Text fontSize="sm" color="gray.600">
                          {field.key} • {field.type}
                          {field.isRequired && " • Required"}
                        </Text>
                      </VStack>
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => removeField(globalIndex)}
                        aria-label="Remove field"
                      />
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          </Box>
        )}
      </VStack>
    );
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>
          <HStack justify="space-between">
            <Text>
              {collection
                ? t("collection.edit") || "Edit Collection"
                : t("collection.create") || "Create Collection"}
            </Text>
            <Button onClick={handleSave} colorScheme="blue">
              {collection ? t("update") || "Update" : t("create") || "Create"}
            </Button>
          </HStack>
        </DrawerHeader>

        <DrawerBody>
          <VStack spacing={6} align="stretch">
            {/* Basic Collection Information */}
            <Grid templateColumns="1fr 1fr" gap={4}>
              <FormControl>
                <FormLabel>{t("collection.name") || "Name"}</FormLabel>
                <Input
                  value={newCollection.name}
                  onChange={(e) =>
                    setNewCollection((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder={
                    t("collection.enterName") || "Enter collection name"
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>{t("type") || "Type"}</FormLabel>
                <Select
                  value={newCollection.type}
                  onChange={(e) =>
                    setNewCollection((prev) => ({
                      ...prev,
                      type: e.target.value as any,
                    }))
                  }
                >
                  <option value={Method.POST}>
                    {t("collection.type.post") || "Post"}
                  </option>
                  <option value={Method.GET}>
                    {t("collection.type.get") || "Get"}
                  </option>
                  <option value={Method.PUT}>
                    {t("collection.type.put") || "Put"}
                  </option>
                  <option value={Method.DELETE}>
                    {t("collection.type.delete") || "Delete"}
                  </option>
                </Select>
              </FormControl>
            </Grid>

            <FormControl>
              <FormLabel>
                {t("collection.description") || "Description"}
              </FormLabel>
              <Textarea
                value={newCollection.description}
                onChange={(e) =>
                  setNewCollection((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder={
                  t("collection.enterDescription") || "Enter description"
                }
                rows={3}
              />
            </FormControl>

            <FormControl>
              <FormLabel>{t("collection.path") || "Path"}</FormLabel>
              <Textarea
                value={newCollection.path}
                onChange={(e) =>
                  setNewCollection((prev) => ({
                    ...prev,
                    path: e.target.value,
                  }))
                }
                placeholder={t("collection.enterPath") || "Enter path"}
                rows={1}
              />
            </FormControl>

            <FormControl>
              <FormLabel>{t("collection.isActive") || "Active"}</FormLabel>
              <Switch
                isChecked={newCollection.isActive}
                onChange={(e) =>
                  setNewCollection((prev) => ({
                    ...prev,
                    isActive: e.target.checked,
                  }))
                }
              />
            </FormControl>

            {/* Header and Body Sections */}
            <Box>
              <Text fontWeight="semibold" mb={4}>
                {t("collection.fields") || "Fields"}
              </Text>

              <Tabs index={activeTab} onChange={setActiveTab}>
                <TabList>
                  <Tab>
                    {t("collection.header") || "Header"} ({headerFields.length})
                  </Tab>
                  <Tab>
                    {t("collection.body") || "Body"} ({bodyFields.length})
                  </Tab>
                </TabList>

                <TabPanels>
                  <TabPanel>{renderFieldSection(false)}</TabPanel>
                  <TabPanel>{renderFieldSection(true)}</TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
